import React, { useCallback, useState } from 'react';

import { noNeedForPopup, requestPopup } from '@eika-infrastruktur/popup-lib';
import { LinkButton } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { WarningCard } from '@eika/card';
import { RichIconTypes } from '@eika/rich-icon';

import ANALYTICS_CATEGORIES from '../../analytics/categories';
import { EikaModal } from '../../components/EikaModal';
import { hasSigningError } from '../../components/transaction/Payment/PaymentForm';
import { useConfig } from '../../defaultConfig';
import { useConfirmedStatus, useContactInformation } from '../../fetchHooks/useContact';
import { FormContextProvider, useFormContext } from '../../hooks/useForm';
import useTranslation from '../../hooks/useTranslation';
import SigningContextFlow from '../../signingContext/SigningContextFlow';
import { SigningContextProvider, useSigningContext } from '../../signingContext/signingContext';
import { Actions } from '../../signingContext/signingTypes';
import { userTrackingIDs } from '../../utils/gtm/trackIDs/user';
import UpdateContactInformationModal from '../ContactInformation/UpdateContactInformationModal';
import createUpdateContactInfoRequest from '../ContactInformation/createUpdateContactInfoRequest';
import { UpdateContactInfoAction, UpdateContactInfoActionType } from '../ContactInformation/form/actions';
import updateContactInfoReducer from '../ContactInformation/form/reducer';
import { updateContactInfoInitialState, UpdateContactInfoState } from '../ContactInformation/form/state';
import useContactInfoFormContext from '../ContactInformation/form/useContactInfoFormContext';
import { Error, useValidate } from '../ContactInformation/form/validate';
import { removeWhitespaceOnSave } from '../ContactInformation/format';
import PeriodicControlModalBody from './PeriodicControlModalBody';
import PeriodicControlSkeleton from './PeriodicControlSkeleton';

import '../ContactInformation/ContactInformation.scss';
import './PeriodicControl.scss';

export const PeriodicControl: React.FC = () => {
    const { handleChange } = useFormContext<
        UpdateContactInfoState,
        UpdateContactInfoAction,
        Error,
        UpdateContactInfoActionType
    >();

    const [open, setOpen] = useState(false);
    const { featureToggles } = useConfig();

    const periodicControl = useConfirmedStatus({
        enabled: featureToggles.enablePeriodicControl,
        onSuccess: (data) => {
            if (data) {
                requestPopup('contactInformation', () => setOpen(true));
            } else {
                noNeedForPopup('contactInformation');
            }
        },
        onError: () => {
            noNeedForPopup('contactInformation');
        },
    });

    const contactInfo = useContactInformation({
        enabled: featureToggles.enablePeriodicControl && open,
        onSuccess: (data) => {
            handleChange({ type: 'email', payload: data.email?.value });
            handleChange({ type: 'mobile', payload: data.phoneNumber?.value });
        },
    });

    const { state } = useContactInfoFormContext();

    const {
        startSigning,
        signingOperationPending,
        signingStatus,
        signingError,
        showSigningFlow,
        signingCompleted,
        dispatch,
    } = useSigningContext(
        createUpdateContactInfoRequest(
            contactInfo.data,
            featureToggles.enableUpdatingEmail,
            featureToggles.enableUpdatingMobilenumber,
        ),
    );

    const onSubmit = useCallback(async () => {
        if (!signingOperationPending) {
            const cleanedEmailValue = removeWhitespaceOnSave(contactInfo.data?.email?.value ?? '');
            const cleanedMobileValue = removeWhitespaceOnSave(contactInfo.data?.phoneNumber?.value ?? '');

            handleChange({ type: 'email', payload: cleanedEmailValue });
            handleChange({ type: 'mobile', payload: cleanedMobileValue });
            startSigning({ ...state, email: cleanedEmailValue, mobile: cleanedMobileValue });
        }
    }, [
        signingOperationPending,
        contactInfo.data?.email?.value,
        contactInfo.data?.phoneNumber?.value,
        handleChange,
        startSigning,
        state,
    ]);

    const translation = useTranslation();

    const [openUpdate, setOpenUpdate] = useState(false);

    const resetState = useCallback(
        (email?: string, mobile?: string) => {
            handleChange({ type: 'mobile', payload: mobile ?? contactInfo.data?.phoneNumber?.value });
            handleChange({ type: 'email', payload: email ?? contactInfo.data?.email?.value });
        },
        [handleChange, contactInfo],
    );

    const openUpdateModal = useCallback(() => {
        setOpenUpdate(true);
        resetState();
    }, [setOpenUpdate, resetState]);

    const resetStates = useCallback(() => {
        contactInfo.invalidateQuery();
        resetState();
    }, [resetState, contactInfo]);

    const invalidateAndReset = useCallback(() => {
        periodicControl.invalidateQuery();
        resetStates();
    }, [periodicControl, resetStates]);

    const retrySigning = () => {
        if (featureToggles.enableUpdatingEmail || featureToggles.enableUpdatingMobilenumber) {
            dispatch({ type: Actions.CLEAR_ERROR });
            const cleanedEmailValue = removeWhitespaceOnSave(state.email ?? '');
            const cleanedMobileValue = removeWhitespaceOnSave(state.mobile ?? '');
            startSigning({ ...state, email: cleanedEmailValue, mobile: cleanedMobileValue });
        }
    };

    const isSigningError = signingError || hasSigningError(signingStatus);

    const showFooter = !signingCompleted && !isSigningError && !showSigningFlow && !contactInfo.isLoading;

    return (
        <EikaModal
            id="periodic-control-contact-info"
            className="contact-information__container"
            open={open}
            setOpen={setOpen}
            onClose={invalidateAndReset}
            hideCloseButton={!signingError}
            disableEscapeKeydownOnHiddenCloseButton={!signingError}
            contentShouldStretch
            footerContent={
                showFooter && (
                    <div className="periodic-control__footer">
                        <LinkButton
                            onClick={openUpdateModal}
                            data-track-id={userTrackingIDs.periodicControl.updateId}
                            data-track-linkname={translation.periodicControl.noEdit}
                        >
                            {translation.periodicControl.noEdit}
                        </LinkButton>
                        <ButtonWithSpinner
                            buttonType="positive"
                            showSpinner={signingOperationPending}
                            onClick={onSubmit}
                            data-track-id={userTrackingIDs.periodicControl.id}
                            data-track-linkname={translation.periodicControl.yesCorrect}
                        >
                            {translation.periodicControl.yesCorrect}
                        </ButtonWithSpinner>
                    </div>
                )
            }
        >
            {contactInfo.isLoading && <PeriodicControlSkeleton />}
            {!signingError && !contactInfo.isLoading && (
                <SigningContextFlow onClosePayment={() => setOpen(false)}>
                    <PeriodicControlModalBody
                        ifSuccess={signingStatus === 'COMPLETED'}
                        contactInfo={contactInfo}
                        setOpen={setOpen}
                    />
                    <UpdateContactInformationModal
                        open={openUpdate && signingStatus !== 'COMPLETED'}
                        setOpen={setOpenUpdate}
                        onClose={() => {
                            setOpenUpdate(false);
                            resetStates();
                        }}
                        isLoading={contactInfo.isLoading}
                        contactInfo={contactInfo.data}
                        isPeriodicControl
                        modalId="periodic-control-update-contact-info"
                    />
                </SigningContextFlow>
            )}
            {isSigningError && (
                <WarningCard
                    className="contact-information__error-wrapper"
                    title={translation.errors.ohNo}
                    isModal
                    icon={RichIconTypes.UNDRENDE}
                >
                    <div className="contact-information__sign-error-message">
                        <p className="typography-paragraph">{translation.contactInfo.signingError.message}</p>
                        <p className="typography-paragraph">{translation.contactInfo.signingError.contactMessage}</p>
                    </div>
                    <div className="error-button-wrapper">
                        <LinkButton onClick={() => setOpen(false)}>{translation.cancel}</LinkButton>
                        <ButtonWithSpinner
                            showSpinner={signingOperationPending}
                            buttonType="positive"
                            onClick={retrySigning}
                        >
                            {translation.tryAgain}
                        </ButtonWithSpinner>
                    </div>
                </WarningCard>
            )}
        </EikaModal>
    );
};

export default () => (
    <SigningContextProvider analyticsCategory={ANALYTICS_CATEGORIES.UPDATE_CONTACT_INFO}>
        <FormContextProvider
            validateHook={useValidate}
            reducer={updateContactInfoReducer}
            initialState={updateContactInfoInitialState}
        >
            <PeriodicControl />
        </FormContextProvider>
    </SigningContextProvider>
);
